<template>
  <div class="ws-tabs" role="tablist">
    <div class="ws-tabs__scroll">
      <div
        v-for="tab in layoutTabs"
        :key="tab.id"
        role="tab"
        class="ws-tabs__tab"
        :class="{ 'is-active': tab.id === activeTabId }"
        :title="tab.name"
      >
        <button
          type="button"
          class="ws-tabs__select"
          @click="select(tab)"
          @dblclick.stop="openRename(tab)"
        >
          <span class="ws-tabs__label">{{ tab.name }}</span>
        </button>
      </div>
    </div>
    <button
      type="button"
      class="ws-tabs__add"
      title="New layout tab"
      @click="add"
    >
      <i class="fas fa-plus"></i>
    </button>

    <modal name="rename-layout-tab" height="auto" :width="420" :adaptive="true">
      <div class="p-4">
        <h5 class="mb-3">Rename Tab</h5>
        <div class="form-group">
          <label class="small text-muted">Name</label>
          <input
            ref="renameInput"
            v-model="renameForm.name"
            class="form-control"
            maxlength="80"
            placeholder="Tab name"
            @keyup.enter="saveRename"
          >
        </div>
        <div class="d-flex justify-content-between align-items-center">
          <button
            v-if="canDeleteRenameTab"
            type="button"
            class="btn btn-outline-danger btn-sm"
            @click="deleteRenameTab"
          >
            <i class="fas fa-trash-alt mr-1"></i> Delete tab
          </button>
          <span v-else></span>
          <div class="d-flex">
            <button type="button" class="btn btn-light mr-2" @click="$modal.hide('rename-layout-tab')">Cancel</button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!renameForm.name.trim()"
              @click="saveRename"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  data() {
    return {
      renameForm: { id: null, name: '' },
    }
  },
  computed: {
    ...mapGetters('workspace', ['layoutTabs', 'activeTabId']),
    canDeleteRenameTab() {
      return (this.layoutTabs || []).length > 1 && !!this.renameForm.id
    },
  },
  methods: {
    ...mapActions('workspace', [
      'setActiveLayoutTab',
      'addLayoutTab',
      'removeLayoutTab',
      'renameLayoutTab',
      'flushSave',
    ]),
    async select(tab) {
      if (!tab || tab.id === this.activeTabId) return
      await this.flushSave()
      this.setActiveLayoutTab(tab.id)
    },
    add() {
      const n = (this.layoutTabs || []).length + 1
      this.addLayoutTab(`Layout ${n}`)
    },
    openRename(tab) {
      if (!tab) return
      if (tab.id !== this.activeTabId) this.setActiveLayoutTab(tab.id)
      this.renameForm = { id: tab.id, name: tab.name || '' }
      this.$modal.show('rename-layout-tab')
      this.$nextTick(() => {
        this.$refs.renameInput?.focus?.()
        this.$refs.renameInput?.select?.()
      })
    },
    saveRename() {
      const name = this.renameForm.name.trim()
      if (!name || !this.renameForm.id) return
      this.renameLayoutTab({ id: this.renameForm.id, name })
      this.$modal.hide('rename-layout-tab')
    },
    deleteRenameTab() {
      if (!this.canDeleteRenameTab) return
      const tab = (this.layoutTabs || []).find((t) => t.id === this.renameForm.id)
      if (!tab) return
      if (!window.confirm(`Delete tab “${tab.name}”?`)) return
      this.removeLayoutTab(tab.id)
      this.$modal.hide('rename-layout-tab')
    },
  },
}
</script>

<style scoped>
.ws-tabs {
  display: flex;
  align-items: stretch;
  gap: 0;
  margin-bottom: 0;
  background: var(--ws-widget-header-bg, #f8f9fa);
  border: 1px solid var(--ws-widget-border, #e9ecef);
  border-radius: 8px;
  overflow: hidden;
  min-height: 40px;
}
.ws-tabs__scroll {
  display: flex;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}
.ws-tabs__tab {
  display: inline-flex;
  align-items: stretch;
  max-width: 200px;
  border-right: 1px solid var(--ws-widget-border, #e9ecef);
  flex-shrink: 0;
}
.ws-tabs__tab:hover,
.ws-tabs__tab.is-active {
  background: var(--ws-ctrl-hover, #dfe3ea);
}
.ws-tabs__tab.is-active {
  background: var(--ws-widget-bg, #fff);
  box-shadow: inset 0 -2px 0 var(--ws-accent, #3d7eff);
}
.ws-tabs__select {
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--ws-text-muted, #6c757d);
  display: inline-flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  padding: 0.55rem 0.85rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  text-align: left;
}
.ws-tabs__tab:hover .ws-tabs__select,
.ws-tabs__tab.is-active .ws-tabs__select {
  color: var(--ws-text, #272b41);
}
.ws-tabs__tab.is-active .ws-tabs__select {
  font-weight: 650;
}
.ws-tabs__label {
  overflow: hidden;
  text-overflow: ellipsis;
}
.ws-tabs__add {
  appearance: none;
  border: 0;
  border-left: 1px solid var(--ws-widget-border, #e9ecef);
  background: transparent;
  color: var(--ws-text-muted, #6c757d);
  width: 40px;
  flex-shrink: 0;
  cursor: pointer;
}
.ws-tabs__add:hover:not(:disabled) {
  background: var(--ws-ctrl-hover, #dfe3ea);
  color: var(--ws-text, #272b41);
}

@media (max-width: 1199.98px) {
  .ws-tabs__tab {
    max-width: 160px;
  }
}
</style>
